import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  useRouterState,
  HeadContent,
  Scripts,
  type ErrorComponentProps,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import appCss from "../styles.css?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { WhatsAppFloat } from "../components/WhatsAppFloat";
import { SITE } from "../lib/site";
import iconAsset from "../assets/svg-icon.png.asset.json";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: ErrorComponentProps) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { name: "google-site-verification", content: "mbOle_AHqIBpzTfIBnAz7hQIa-j31uw-4krXLv1ERBg" },
      { name: "yandex-verification", content: "6575d697d08d1711" },
      { title: "SVG Multimídia | SEO, Sites e Ficha do Google para Empresas" },
      { name: "description", content: SITE.description },
      { name: "author", content: SITE.name },
      { name: "theme-color", content: "#061224" },
      { name: "geo.region", content: "BR-RS" },
      { name: "geo.placename", content: "Frederico Westphalen" },
      { name: "geo.position", content: "-27.357745;-53.392968" },
      { name: "ICBM", content: "-27.357745, -53.392968" },
      { property: "og:site_name", content: SITE.name },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "pt_BR" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:title", content: "SVG Multimídia | SEO, Sites e Ficha do Google para Empresas" },
      { name: "twitter:title", content: "SVG Multimídia | SEO, Sites e Ficha do Google para Empresas" },
      { property: "og:description", content: SITE.description },
      { name: "twitter:description", content: SITE.description },
      { property: "og:image", content: "https://pub-bb2e103a32db4e198524a2e9ed8f35b4.r2.dev/58f1d451-af6c-42e1-8a4d-4759863f5002/id-preview-d443b8f9--4e628cc3-9baa-4a87-b23b-02ac04e85c33.lovable.app-1782452598792.png" },
      { name: "twitter:image", content: "https://pub-bb2e103a32db4e198524a2e9ed8f35b4.r2.dev/58f1d451-af6c-42e1-8a4d-4759863f5002/id-preview-d443b8f9--4e628cc3-9baa-4a87-b23b-02ac04e85c33.lovable.app-1782452598792.png" },

    ],
    links: [
      { rel: "icon", href: iconAsset.url, type: "image/png" },
      { rel: "apple-touch-icon", href: iconAsset.url },
      {
        rel: "stylesheet",
        href: appCss,
      },
    ],
    scripts: [
      {
        src: "https://www.googletagmanager.com/gtag/js?id=G-5PLZFYEFQZ",
        async: true,
      },
      {
        children:
          "window.dataLayer = window.dataLayer || [];function gtag(){dataLayer.push(arguments);}gtag('js', new Date());gtag('config', 'G-5PLZFYEFQZ');",
      },
      {
        src: "https://www.googletagmanager.com/gtag/js?id=AW-17558535745",
        async: true,
      },
      {
        children: "gtag('config', 'AW-17558535745');",
      },
      {
        children:
          "(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','GTM-NGRC7QKF');",
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Organization",
              "@id": "https://www.svgmultimidia.com.br/#organization",
              name: SITE.name,
              url: "https://www.svgmultimidia.com.br/",
              logo: "https://www.svgmultimidia.com.br" + iconAsset.url,
              description: SITE.description,
              email: SITE.email,
              telephone: "+55" + SITE.whatsappNumber,
              areaServed: "BR",
              founder: [
                { "@type": "Person", name: "Mário Jorge Savanhago Júnior" },
                { "@type": "Person", name: "Luiz Gustavo Prestes" },
              ],
              sameAs: [SITE.social.instagram, SITE.social.youtube, SITE.social.facebook],
            },
            {
              "@type": "WebSite",
              "@id": "https://www.svgmultimidia.com.br/#website",
              url: "https://www.svgmultimidia.com.br/",
              name: SITE.name,
              description: SITE.description,
              inLanguage: "pt-BR",
              publisher: { "@id": "https://www.svgmultimidia.com.br/#organization" },
              potentialAction: {
                "@type": "SearchAction",
                target: "https://www.svgmultimidia.com.br/blog?q={search_term_string}",
                "query-input": "required name=search_term_string",
              },
            },
            {
              "@type": "LocalBusiness",
              "@id": "https://www.svgmultimidia.com.br/#localbusiness",
              name: SITE.name,
              image: "https://www.svgmultimidia.com.br" + iconAsset.url,
              url: "https://www.svgmultimidia.com.br/",
              telephone: "+55" + SITE.whatsappNumber,
              email: SITE.email,
              priceRange: "$$",
              address: {
                "@type": "PostalAddress",
                addressLocality: "Frederico Westphalen",
                addressRegion: "RS",
                addressCountry: "BR",
              },
              geo: { "@type": "GeoCoordinates", latitude: -27.357745, longitude: -53.392968 },
              areaServed: { "@type": "Country", name: "Brasil" },
              openingHoursSpecification: [
                {
                  "@type": "OpeningHoursSpecification",
                  dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
                  opens: "08:00",
                  closes: "18:00",
                },
              ],
              sameAs: [SITE.social.instagram, SITE.social.youtube, SITE.social.facebook],
            },
          ],
        }),
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="pt-BR">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  // Landing pages de venda usam cabeçalho/rodapé próprios.
  const bareLayout = pathname.startsWith("/venda-no-whatsapp-metodo-conversa");

  return (
    <QueryClientProvider client={queryClient}>
      <div className="flex min-h-screen flex-col">
        {!bareLayout && <Header />}
        <main className="flex-1">
          <Outlet />
        </main>
        {!bareLayout && <Footer />}
        {!bareLayout && <WhatsAppFloat />}
      </div>
    </QueryClientProvider>
  );
}
